import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { RALLY_STATUS_OPTIONS } from "../../utils/rally.utils";
import CategorySelectList from "../../_components/CategorySelectList";

const RallyForm = ({
  form,
  validationErrors,
  submitting,
  submitLabel = "Create Rally",
  submittingLabel = "Creating...",
  onSubmit,
  onFieldChange,
  onHighlightChange,
  onAddHighlight,
  onRemoveHighlight,
  onBack,
  categories = [],
  isCategoriesLoading = false,
  isCategoriesError = false,
}) => {
  return (
    <Form onSubmit={onSubmit}>
      <h5 className="mb-3 mt-2">Basic Information</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.name}
              onChange={(e) => onFieldChange("name", e.target.value)}
              placeholder="Cholistan Desert Rally 2026"
              isInvalid={!!validationErrors.name}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.name}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Edition Number</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.edition_number}
              onChange={(e) => onFieldChange("edition_number", e.target.value)}
              placeholder="21"
              isInvalid={!!validationErrors.edition_number}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.edition_number}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Location <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.location}
              onChange={(e) => onFieldChange("location", e.target.value)}
              placeholder="Cholistan Desert"
              isInvalid={!!validationErrors.location}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.location}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Organiser</Form.Label>
            <Form.Control
              type="text"
              value={form.organiser}
              onChange={(e) => onFieldChange("organiser", e.target.value)}
              placeholder="TDCP"
            />
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Short description of the rally"
            />
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Categories</h5>
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Assign categories</Form.Label>
            <CategorySelectList
              categories={categories}
              selectedIds={form.categories || []}
              onChange={(next) => {
                onFieldChange("categories", next);
                const nextPricing = { ...(form.pricing || {}) };
                Object.keys(nextPricing).forEach((categoryId) => {
                  if (!next.includes(categoryId)) delete nextPricing[categoryId];
                });
                onFieldChange("pricing", nextPricing);
              }}
              pricing={form.pricing || {}}
              onPricingChange={(categoryId, amount) =>
                onFieldChange("pricing", {
                  ...(form.pricing || {}),
                  [categoryId]: amount,
                })
              }
              pricingErrors={validationErrors.pricing || {}}
              disabled={submitting}
              isLoading={isCategoriesLoading}
              isError={isCategoriesError}
            />
            <Form.Text className="text-muted">
              Each selected category needs a price. You can select more than
              one.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Dates</h5>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>
              Start Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              value={form.date}
              onChange={(e) => onFieldChange("date", e.target.value)}
              isInvalid={!!validationErrors.date}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.date}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={form.end_date}
              onChange={(e) => onFieldChange("end_date", e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Rally Start Date</Form.Label>
            <Form.Control
              type="date"
              value={form.rally_start_date}
              onChange={(e) => onFieldChange("rally_start_date", e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Registration Start</Form.Label>
            <Form.Control
              type="date"
              value={form.registration_start_date}
              onChange={(e) =>
                onFieldChange("registration_start_date", e.target.value)
              }
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Registration End</Form.Label>
            <Form.Control
              type="date"
              value={form.registration_end_date}
              onChange={(e) =>
                onFieldChange("registration_end_date", e.target.value)
              }
            />
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Status & Visibility</h5>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={form.status}
              onChange={(e) => onFieldChange("status", e.target.value)}
            >
              {RALLY_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4} className="d-flex align-items-center">
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="is_featured"
              label="Featured rally"
              checked={Boolean(form.is_featured)}
              onChange={(e) => onFieldChange("is_featured", e.target.checked)}
            />
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Highlights</h5>
      <Row>
        <Col md={12}>
          {(form.highlights || []).map((highlight, index) => (
            <div key={`highlight-${index}`} className="d-flex gap-2 mb-2">
              <Form.Control
                type="text"
                value={highlight}
                onChange={(e) => onHighlightChange(index, e.target.value)}
                placeholder={`Highlight ${index + 1}`}
              />
              <Button
                type="button"
                variant="outline-danger"
                disabled={(form.highlights || []).length <= 1}
                onClick={() => onRemoveHighlight(index)}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline-primary"
            className="mb-3"
            onClick={onAddHighlight}
          >
            + Add Highlight
          </Button>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Stats</h5>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Drivers</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.stats_drivers}
              onChange={(e) => onFieldChange("stats_drivers", e.target.value)}
              isInvalid={!!validationErrors.stats_drivers}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.stats_drivers}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Events</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.stats_events}
              onChange={(e) => onFieldChange("stats_events", e.target.value)}
              isInvalid={!!validationErrors.stats_events}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.stats_events}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Participants</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.stats_participants}
              onChange={(e) =>
                onFieldChange("stats_participants", e.target.value)
              }
              isInvalid={!!validationErrors.stats_participants}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.stats_participants}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button type="button" variant="outline-secondary" onClick={onBack}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {submittingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </Form>
  );
};

export default RallyForm;
